import { getErrorTypeFromUnknown } from "./ErrorParsers";

/**
 * Top-level framework error categories carried in `ErrorWithDebugInfos.type`.
 *
 * These are the broad transport-visible buckets. They are stable string
 * discriminators that travel across process and network boundaries.
 *
 * More specific framework failure reasons use `debugInfos.errorCode` and are
 * represented via `InternalErrors` when the type is `AtomErrors.InternalError`.
 */
export enum AtomErrors {
  CannotProvideAtom = "atom/cannot-provide",
  AtomIsNotRespawnable = "atom/not-respawnable",
  InternalError = "framework/internal",
  FailedToGetLock = "lock/failed-to-get",
  PersistenceServiceCriticalError = "persistence/critical",
  /**
   * A write hit a unique constraint the user can resolve: the value of a
   * `@unique` field or the key of a set is taken, or the identifier of an
   * entity with several parents is held in another place (`AT409`). A race
   * on the id of an entity keeps `PersistenceServiceCriticalError`. Only a code
   * (`debugInfos.errorCode`): the `type` of such an error stays
   * `PersistenceServiceCriticalError`.
   */
  UniqueConstraintViolation = "persistence/unique-violation",
  InsufficientPermission = "permission/insufficient",
  DeadLock = "lock/deadlock",
  /**
   * The row of an atom with pending changes moved since a tracked find read
   * it. `locked()` reruns the attempt, so it reaches no client on its own.
   */
  FoundAtomMoved = "lock/found-atom-moved",
  LockTimedOut = "lock/timed-out",
}

/**
 * Code of the `UserFacingError` with which `patchAtom` rejects a save whose
 * `expected` values are stale: a unit the patch writes was changed by someone
 * else since the value the change was built on. Class `rule`. A client
 * recognises it by this code and reloads the atom before the user decides.
 */
export const ATOM_SAVE_CONFLICT_ERROR_CODE = "atom/save-conflict";

/**
 * Code of the `UserFacingError` with which `exportAtoms` rejects an export
 * that found no rows, when the caller set `requireRows`. Class `rule`.
 */
export const ATOM_EXPORT_NO_ROWS_ERROR_CODE = "atom/export-no-rows";

/**
 * Code of the `UserFacingError` with which a presign of an upload refuses a
 * file type outside the allowed list (`checkUploadRequest` in
 * `@atoms/atom-module`). Class `rule`; params `{ mimeType }`.
 */
export const UPLOAD_TYPE_NOT_ALLOWED_ERROR_CODE = "upload/type-not-allowed";

/**
 * Code of the `UserFacingError` with which a presign of an upload refuses a
 * file larger than allowed (`checkUploadRequest` in `@atoms/atom-module`).
 * Class `rule`; params `{ maxSizeMb }`.
 */
export const UPLOAD_TOO_LARGE_ERROR_CODE = "upload/too-large";

/**
 * Prefix of the key under which an error with the code
 * `AtomErrors.UniqueConstraintViolation` names its constraint to the client:
 * `app.errors.unique.<constraint>`. A project gives the constraint a sentence
 * by adding that key to its i18n catalog.
 */
export const UNIQUE_VIOLATION_USER_MESSAGE_KEY_PREFIX = "app.errors.unique.";

const atomErrorTypes = new Set<string>(Object.values(AtomErrors));

export function getAtomErrorType(error: unknown): AtomErrors | undefined {
  const errorType = getErrorTypeFromUnknown(error);
  if (!errorType || !atomErrorTypes.has(errorType)) {
    return undefined;
  }

  return errorType as AtomErrors;
}
